/**
 * Google Play Billing via Digital Goods API
 * Used when the app is running inside a TWA (Trusted Web Activity) from the Play Store.
 * Falls back to Stripe when running in a regular browser.
 */
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

// Google Play SKU IDs — loaded dynamically from admin config
// Fallback defaults if config hasn't been set yet
const DEFAULT_PLAY_SKUS = {
  paid: 'eili_standard_monthly',
  premium: 'eili_premium_monthly',
  share: 'eili_share_monthly',
};

let _playSkus = null;

/**
 * Load Play SKU mappings from the backend (admin-configurable)
 */
async function loadPlaySkus() {
  if (_playSkus) return _playSkus;
  try {
    const res = await axios.get(`${API}/store-config`);
    const plans = res.data?.plans || [];
    const skuMap = {};
    plans.forEach(p => {
      if (p.key && p.play_sku) skuMap[p.key] = p.play_sku;
    });
    _playSkus = Object.keys(skuMap).length > 0 ? skuMap : DEFAULT_PLAY_SKUS;
  } catch {
    _playSkus = DEFAULT_PLAY_SKUS;
  }
  return _playSkus;
}

export { DEFAULT_PLAY_SKUS as PLAY_SKUS };

let _dgService = null;
let _isTwaChecked = false;
let _isTwa = false;

/**
 * Detect if we're running inside a TWA (Trusted Web Activity)
 */
export function isTWA() {
  if (_isTwaChecked) return _isTwa;
  _isTwaChecked = true;

  // Method 1: document.referrer from android-app://
  if (document.referrer.startsWith('android-app://')) {
    _isTwa = true;
    return true;
  }

  // Method 2: Check for standalone display mode (PWA installed from Play Store)
  if (window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches) {
    // Could be PWA or TWA — check for Digital Goods API support to confirm TWA
    if ('getDigitalGoodsService' in window) {
      _isTwa = true;
      return true;
    }
  }

  // Method 3: Check for TWA-specific APIs
  if ('getDigitalGoodsService' in window) {
    _isTwa = true;
    return true;
  }

  return false;
}

/**
 * Check if Google Play Billing is available
 */
export async function isPlayBillingAvailable() {
  if (!('getDigitalGoodsService' in window)) return false;
  try {
    _dgService = await window.getDigitalGoodsService('https://play.google.com/billing');
    return !!_dgService;
  } catch {
    return false;
  }
}

/**
 * Get the Digital Goods Service instance
 */
async function getService() {
  if (_dgService) return _dgService;
  _dgService = await window.getDigitalGoodsService('https://play.google.com/billing');
  return _dgService;
}

/**
 * Fetch product details from Google Play for the given SKU IDs
 */
export async function getPlayProductDetails(skuIds) {
  try {
    const service = await getService();
    const details = await service.getDetails(skuIds);
    return details;
  } catch (err) {
    console.error('[PlayBilling] Failed to get product details:', err);
    return [];
  }
}

/**
 * Purchase a subscription via Google Play Billing
 * @param {string} planKey - One of 'paid', 'premium', 'share'
 * @returns {object|null} - Purchase result or null if failed/cancelled
 */
export async function purchaseWithPlayBilling(planKey) {
  const skus = await loadPlaySkus();
  const sku = skus[planKey];
  if (!sku) throw new Error(`No Play SKU mapped for plan: ${planKey}. Configure it in Admin → Store & Billing.`);

  const service = await getService();

  // Get item details from Play
  const details = await service.getDetails([sku]);
  if (!details || details.length === 0) {
    throw new Error(`Product ${sku} not found in Google Play. Make sure it's configured in the Play Console.`);
  }

  const item = details[0];

  // Build the PaymentRequest
  const paymentMethods = [{
    supportedMethods: 'https://play.google.com/billing',
    data: { sku: item.itemId },
  }];

  const paymentDetails = {
    total: {
      label: item.title || 'Subscription',
      amount: {
        currency: item.price?.currency || 'USD',
        value: item.price?.value || '0.00',
      },
    },
  };

  const request = new PaymentRequest(paymentMethods, paymentDetails);

  // Check if Play Billing is available for this request
  const canPay = await request.canMakePayment();
  if (!canPay) {
    throw new Error('Google Play Billing is not available on this device.');
  }

  // Show the Google Play purchase sheet
  const response = await request.show();

  // Extract the purchase token
  const { purchaseToken, orderId } = response.details || {};

  if (!purchaseToken) {
    await response.complete('fail');
    throw new Error('No purchase token received from Google Play.');
  }

  // Verify the purchase on our backend
  try {
    const verifyResult = await axios.post(
      `${API}/payments/verify-play-purchase`,
      {
        purchase_token: purchaseToken,
        sku: sku,
        plan_key: planKey,
        order_id: orderId,
      },
      { withCredentials: true }
    );

    if (verifyResult.data.verified) {
      // Acknowledge the purchase so Google doesn't auto-refund
      await service.acknowledge(purchaseToken, 'repeating');
      await response.complete('success');
      return {
        success: true,
        plan: planKey,
        purchaseToken,
        orderId,
      };
    } else {
      await response.complete('fail');
      throw new Error('Purchase verification failed on server.');
    }
  } catch (err) {
    await response.complete('fail');
    throw err;
  }
}

/**
 * Check for existing Play Store purchases (e.g., on app startup)
 * Useful for restoring purchases after reinstall
 */
export async function checkExistingPurchases() {
  try {
    const service = await getService();
    const purchases = await service.listPurchases();
    return purchases || [];
  } catch {
    return [];
  }
}
